import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const DependencyMapping = ({ milestones = [], onBack, isMobile = false }) => {
  const [dependencyMap, setDependencyMap] = useState(new Map());
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [viewMode, setViewMode] = useState('graph'); // 'graph' or 'list'

  useEffect(() => {
    // Build dependency map
    const map = new Map();
    
    milestones?.forEach(milestone => {
      const dependencies = milestone?.dependencies || [];
      const dependents = milestones?.filter(m => 
        m?.dependencies?.includes(milestone?.id)
      )?.map(m => m?.id);
      
      map?.set(milestone?.id, {
        milestone,
        dependencies,
        dependents,
        level: calculateLevel(milestone, milestones)
      });
    });
    
    setDependencyMap(map);
  }, [milestones]);

  const calculateLevel = (milestone, allMilestones) => {
    const visited = new Set();
    
    const getMaxDepth = (currentMilestone) => {
      if (visited?.has(currentMilestone?.id)) return 0;
      visited?.add(currentMilestone?.id);
      
      const deps = currentMilestone?.dependencies || [];
      if (deps?.length === 0) return 0;
      
      const depthLevels = deps?.map(depId => {
        const depMilestone = allMilestones?.find(m => m?.id === depId);
        return depMilestone ? getMaxDepth(depMilestone) + 1 : 0;
      });
      
      return Math.max(...depthLevels, 0);
    };
    
    return getMaxDepth(milestone);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return { icon: 'CheckCircle', color: 'text-success' };
      case 'in-progress':
        return { icon: 'Clock', color: 'text-accent' };
      case 'overdue':
        return { icon: 'AlertTriangle', color: 'text-error' };
      default:
        return { icon: 'Circle', color: 'text-muted-foreground' };
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'bg-error/10 text-error border-error/20';
      case 'high':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'medium':
        return 'bg-accent/10 text-accent border-accent/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const hasCyclicalDependency = (milestoneId, targetId, visited = new Set()) => {
    if (visited?.has(milestoneId)) return true;
    if (milestoneId === targetId) return true;
    
    visited?.add(milestoneId);
    
    const milestone = dependencyMap?.get(milestoneId);
    if (!milestone) return false;
    
    return milestone?.dependencies?.some(depId => 
      hasCyclicalDependency(depId, targetId, new Set(visited))
    );
  };

  const renderGraphView = () => {
    const levels = new Map();
    
    // Group milestones by dependency level
    dependencyMap?.forEach((data, milestoneId) => {
      const level = data?.level;
      if (!levels?.has(level)) {
        levels?.set(level, []);
      }
      levels?.get(level)?.push(data);
    });

    const sortedLevels = Array.from(levels?.keys())?.sort((a, b) => a - b);

    return (
      <div className="p-6">
        <div className="mb-6">
          <h4 className="font-medium text-foreground mb-2">Dependency Flow</h4>
          <p className="text-sm text-muted-foreground">
            Milestones are organized by dependency levels. Dependencies flow from left to right.
          </p>
        </div>
        <div className="relative overflow-x-auto">
          <div className="flex space-x-8 min-w-max">
            {sortedLevels?.map(level => (
              <div key={level} className="flex flex-col space-y-4 min-w-[200px]">
                <div className="text-center">
                  <div className="inline-flex items-center space-x-2 px-3 py-1 bg-muted rounded-full text-sm font-medium text-muted-foreground">
                    <Icon name="Layers" size={14} />
                    <span>Level {level}</span>
                  </div>
                </div>
                
                {levels?.get(level)?.map(data => {
                  const { milestone } = data;
                  const statusConfig = getStatusIcon(milestone?.status);
                  const isSelected = selectedMilestone?.id === milestone?.id;
                  
                  return (
                    <div
                      key={milestone?.id}
                      onClick={() => setSelectedMilestone(
                        isSelected ? null : milestone
                      )}
                      className={`p-3 border rounded-lg cursor-pointer transition-legal-fast ${
                        isSelected 
                          ? 'border-primary bg-primary/5' :'border-border bg-card hover:border-accent hover:shadow-legal'
                      }`}
                    >
                      <div className="flex items-center space-x-2 mb-2">
                        <Icon 
                          name={statusConfig?.icon} 
                          size={14} 
                          className={statusConfig?.color} 
                        />
                        <span className="text-sm font-medium text-foreground truncate">
                          {milestone?.title}
                        </span>
                      </div>
                      
                      <div className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getPriorityColor(milestone?.priority)}`}>
                        {milestone?.priority}
                      </div>
                      
                      <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                        <span>{milestone?.assignedParty}</span>
                        <div className="flex items-center space-x-2">
                          {data?.dependencies?.length > 0 && (
                            <div className="flex items-center space-x-1">
                              <Icon name="ArrowLeft" size={10} />
                              <span>{data?.dependencies?.length}</span>
                            </div>
                          )}
                          {data?.dependents?.length > 0 && (
                            <div className="flex items-center space-x-1">
                              <Icon name="ArrowRight" size={10} />
                              <span>{data?.dependents?.length}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        {/* Selected Milestone Details */}
        {selectedMilestone && (
          <div className="mt-8 p-4 border border-primary/20 rounded-lg bg-primary/5">
            <div className="flex items-center justify-between mb-3">
              <h5 className="font-medium text-foreground">{selectedMilestone?.title}</h5>
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setSelectedMilestone(null)}
                iconName="X"
              />
            </div>
            
            <div className="space-y-3">
              {selectedMilestone?.description && (
                <p className="text-sm text-muted-foreground">
                  {selectedMilestone?.description}
                </p>
              )}
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-foreground">Due Date:</span>
                  <span className="ml-2 text-muted-foreground">
                    {new Date(selectedMilestone?.dueDate)?.toLocaleDateString()}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-foreground">Assigned to:</span>
                  <span className="ml-2 text-muted-foreground">
                    {selectedMilestone?.assignedParty}
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h6 className="font-medium text-foreground mb-2">Dependencies</h6>
                  {selectedMilestone?.dependencies?.length > 0 ? (
                    <div className="space-y-1">
                      {selectedMilestone?.dependencies?.map(depId => {
                        const depMilestone = milestones?.find(m => m?.id === depId);
                        return depMilestone ? (
                          <div key={depId} className="text-xs text-muted-foreground">
                            • {depMilestone?.title}
                          </div>
                        ) : null;
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">No dependencies</p>
                  )}
                </div>
                
                <div>
                  <h6 className="font-medium text-foreground mb-2">Dependents</h6>
                  {dependencyMap?.get(selectedMilestone?.id)?.dependents?.length > 0 ? (
                    <div className="space-y-1">
                      {dependencyMap?.get(selectedMilestone?.id)?.dependents?.map(depId => {
                        const depMilestone = milestones?.find(m => m?.id === depId);
                        return depMilestone ? (
                          <div key={depId} className="text-xs text-muted-foreground">
                            • {depMilestone?.title}
                          </div>
                        ) : null;
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">No dependents</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderListView = () => {
    const sortedMilestones = [...milestones]?.sort((a, b) => 
      new Date(a?.dueDate) - new Date(b?.dueDate)
    );

    return (
      <div className="p-6">
        <div className="mb-6">
          <h4 className="font-medium text-foreground mb-2">Dependency List</h4>
          <p className="text-sm text-muted-foreground">
            All milestones with their dependency relationships listed chronologically.
          </p>
        </div>
        <div className="space-y-4">
          {sortedMilestones?.map(milestone => {
            const statusConfig = getStatusIcon(milestone?.status);
            const dependencies = milestone?.dependencies || [];
            const dependents = dependencyMap?.get(milestone?.id)?.dependents || [];
            
            return (
              <div key={milestone?.id} className="p-4 border border-border rounded-lg bg-card">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <Icon 
                      name={statusConfig?.icon} 
                      size={16} 
                      className={statusConfig?.color} 
                    />
                    <h5 className="font-medium text-foreground">{milestone?.title}</h5>
                    <div className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(milestone?.priority)}`}>
                      {milestone?.priority}
                    </div>
                  </div>
                  
                  <div className="text-sm text-muted-foreground">
                    Due: {new Date(milestone?.dueDate)?.toLocaleDateString()}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h6 className="text-sm font-medium text-foreground mb-2">
                      Blocked by ({dependencies?.length}):
                    </h6>
                    {dependencies?.length > 0 ? (
                      <div className="space-y-1">
                        {dependencies?.map(depId => {
                          const depMilestone = milestones?.find(m => m?.id === depId);
                          const depStatus = depMilestone ? getStatusIcon(depMilestone?.status) : null;
                          
                          return depMilestone ? (
                            <div key={depId} className="flex items-center space-x-2 text-sm">
                              <Icon 
                                name={depStatus?.icon} 
                                size={12} 
                                className={depStatus?.color} 
                              />
                              <span className="text-muted-foreground">
                                {depMilestone?.title}
                              </span>
                            </div>
                          ) : null;
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No blocking dependencies</p>
                    )}
                  </div>
                  
                  <div>
                    <h6 className="text-sm font-medium text-foreground mb-2">
                      Blocks ({dependents?.length}):
                    </h6>
                    {dependents?.length > 0 ? (
                      <div className="space-y-1">
                        {dependents?.map(depId => {
                          const depMilestone = milestones?.find(m => m?.id === depId);
                          const depStatus = depMilestone ? getStatusIcon(depMilestone?.status) : null;
                          
                          return depMilestone ? (
                            <div key={depId} className="flex items-center space-x-2 text-sm">
                              <Icon 
                                name={depStatus?.icon} 
                                size={12} 
                                className={depStatus?.color} 
                              />
                              <span className="text-muted-foreground">
                                {depMilestone?.title}
                              </span>
                            </div>
                          ) : null;
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No dependent milestones</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={`${isMobile ? 'p-4' : 'h-full flex flex-col'}`}>
      {/* Header */}
      <div className={`${isMobile ? 'mb-6' : 'p-6 border-b border-border'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Dependency Mapping</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Visualize milestone relationships and dependencies
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="flex items-center bg-muted rounded-lg p-1">
              <Button
                variant={viewMode === 'graph' ? 'default' : 'ghost'}
                size="xs"
                onClick={() => setViewMode('graph')}
                iconName="GitBranch"
              >
                Graph
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="xs"
                onClick={() => setViewMode('list')}
                iconName="List"
              >
                List
              </Button>
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              iconName="ArrowLeft"
            >
              Back
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={`${isMobile ? '' : 'flex-1 overflow-y-auto'}`}>
        {milestones?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="GitBranch" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Milestones</h3>
            <p className="text-muted-foreground">
              Add milestones to view their dependency relationships.
            </p>
          </div>
        ) : (
          <>
            {viewMode === 'graph' ? renderGraphView() : renderListView()}
          </>
        )}
      </div>
    </div>
  );
};

export default DependencyMapping;